import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  Check, 
  AlertCircle, 
  FileText, 
  Loader2,
  Shield,
  Clock,
  Building2
} from 'lucide-react';
import { FILE_KEY_CONFIGS, getFileKeyConfig } from '@/lib/modules/file-keys';
import { FileKey } from '@/lib/modules/types';

interface UploadedFile {
  id: string;
  fileKey: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  isFromThisLink: boolean;
}

interface PortalData {
  auditId: string;
  auditName: string;
  clientName: string;
  requestedFileKeys: string[] | null;
  uploadedFileKeys: string[];
  uploadedFileCounts: Record<string, number>;
  uploadedFiles: UploadedFile[];
  tokenId: string;
}

interface FileUploadState {
  [key: string]: {
    uploading: boolean;
    progress: number;
    error: string | null;
    success: boolean;
    fileName?: string;
  };
}

export default function ClientUploadPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [uploadState, setUploadState] = useState<FileUploadState>({});

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('Ingen giltig länk');
      setLoading(false);
      return;
    }

    validateToken(token);
  }, [token]);

  const validateToken = async (tokenValue: string) => {
    try {
      const response = await fetch(
        `https://bijyphjgqkqmsrzhiqin.supabase.co/functions/v1/client-upload-validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenValue })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Länken är ogiltig eller har upphört');
        setLoading(false);
        return;
      }

      // Ensure uploadedFileCounts and uploadedFiles exist
      const portalDataWithCounts: PortalData = {
        ...data,
        uploadedFileCounts: data.uploadedFileCounts || {},
        uploadedFiles: data.uploadedFiles || [],
      };

      setPortalData(portalDataWithCounts);
      setLoading(false);
    } catch (err) {
      console.error('Token validation error:', err);
      setError('Ett fel uppstod. Försök igen senare.');
      setLoading(false);
    }
  };

  const handleFileUpload = async (fileKey: string, file: File) => {
    if (!token || !portalData) return;

    // Generate a temporary ID for tracking this specific upload
    const tempId = `temp-${Date.now()}-${file.name}`;

    setUploadState(prev => ({
      ...prev,
      [tempId]: { uploading: true, progress: 0, error: null, success: false, fileName: file.name }
    }));

    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('fileKey', fileKey);
      formData.append('file', file);

      const response = await fetch(
        `https://bijyphjgqkqmsrzhiqin.supabase.co/functions/v1/client-upload-file`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setUploadState(prev => ({
          ...prev,
          [tempId]: { uploading: false, progress: 0, error: data.error, success: false, fileName: file.name }
        }));
        return;
      }

      // Add the new file to the uploaded files list
      const newFile: UploadedFile = {
        id: data.documentId || tempId,
        fileKey: fileKey,
        fileName: file.name,
        sizeBytes: file.size,
        uploadedAt: new Date().toISOString(),
        isFromThisLink: true
      };

      // Update uploaded file keys and counts
      setPortalData(prev => {
        if (!prev) return null;
        const newCounts = { ...prev.uploadedFileCounts };
        newCounts[fileKey] = (newCounts[fileKey] || 0) + 1;
        
        return {
          ...prev,
          uploadedFileKeys: prev.uploadedFileKeys.includes(fileKey) 
            ? prev.uploadedFileKeys 
            : [...prev.uploadedFileKeys, fileKey],
          uploadedFileCounts: newCounts,
          uploadedFiles: [...prev.uploadedFiles, newFile],
        };
      });

      // Remove the temp upload state after success
      setUploadState(prev => {
        const newState = { ...prev };
        delete newState[tempId];
        return newState;
      });

    } catch (err) {
      console.error('Upload error:', err);
      setUploadState(prev => ({
        ...prev,
        [tempId]: { uploading: false, progress: 0, error: 'Uppladdningen misslyckades', success: false, fileName: file.name }
      }));
    }
  };

  // Get file keys to display
  const getDisplayFileKeys = (): FileKey[] => {
    if (portalData?.requestedFileKeys && portalData.requestedFileKeys.length > 0) {
      return portalData.requestedFileKeys as FileKey[];
    }
    // Default to common file types if none specified
    return Object.keys(FILE_KEY_CONFIGS) as FileKey[];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Validerar länk...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Länken är ogiltig</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Kontakta din revisor för att få en ny länk.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!portalData) return null;

  const fileKeys = getDisplayFileKeys();
  // Calculate progress based on categories
  const categoriesWithFiles = fileKeys.filter(k => portalData.uploadedFileKeys.includes(k)).length;
  const totalCategories = fileKeys.length;
  
  // Calculate total files uploaded only for requested categories
  const totalFilesUploaded = fileKeys.reduce((sum, key) => {
    return sum + (portalData.uploadedFileCounts?.[key] || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-semibold text-lg">Säker filuppladdning</span>
          </div>
          <h1 className="text-2xl font-bold">{portalData.auditName}</h1>
          {portalData.clientName && (
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <Building2 className="h-4 w-4" />
              <span>{portalData.clientName}</span>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Progress overview */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Uppladdningsstatus</span>
              <span className="text-sm text-muted-foreground">
                {totalFilesUploaded > 0 
                  ? `${totalFilesUploaded} fil${totalFilesUploaded !== 1 ? 'er' : ''} uppladdade (${categoriesWithFiles} av ${totalCategories} kategorier)`
                  : `${categoriesWithFiles} av ${totalCategories} kategorier klara`
                }
              </span>
            </div>
            <Progress value={(categoriesWithFiles / totalCategories) * 100} className="h-2" />
          </CardContent>
        </Card>

        {/* Info alert */}
        <Alert className="mb-6">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Ladda upp de filer som efterfrågas nedan. Dina filer skickas direkt och säkert till revisorn.
          </AlertDescription>
        </Alert>

        {/* File list */}
        <div className="space-y-4">
          {fileKeys.map((fileKey) => {
            const config = getFileKeyConfig(fileKey);
            const uploadedCount = portalData.uploadedFileCounts?.[fileKey] || 0;
            const hasUploaded = uploadedCount > 0;
            const allowMultiple = config?.allowMultiple ?? false;
            
            // Get files for this category
            const filesForCategory = portalData.uploadedFiles.filter(f => f.fileKey === fileKey);
            
            // Get active uploads for this category
            const activeUploads = Object.entries(uploadState)
              .filter(([key, state]) => key.startsWith('temp-') && state.uploading)
              .map(([, state]) => state);

            return (
              <FileUploadCard
                key={fileKey}
                fileKey={fileKey}
                config={config}
                uploadedCount={uploadedCount}
                hasUploaded={hasUploaded}
                allowMultiple={allowMultiple}
                uploadedFiles={filesForCategory}
                activeUploads={activeUploads}
                onUpload={(files) => files.forEach(file => handleFileUpload(fileKey, file))}
              />
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Denna länk är personlig och ska inte delas med andra.</p>
          <p className="mt-1">Har du frågor? Kontakta din revisor.</p>
        </div>
      </main>
    </div>
  );
}

// File upload card component
interface FileUploadCardProps {
  fileKey: string;
  config: ReturnType<typeof getFileKeyConfig>;
  uploadedCount: number;
  hasUploaded: boolean;
  allowMultiple: boolean;
  uploadedFiles: UploadedFile[];
  activeUploads: Array<{ fileName?: string }>;
  onUpload: (files: File[]) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileUploadCard({ 
  fileKey, 
  config, 
  uploadedCount, 
  hasUploaded, 
  allowMultiple,
  uploadedFiles,
  activeUploads,
  onUpload 
}: FileUploadCardProps) {
  const { t } = useTranslation();
  
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onUpload(acceptedFiles);
    }
  }, [onUpload]);

  // Determine if upload is allowed
  const canUpload = allowMultiple || !hasUploaded;
  const isUploading = activeUploads.length > 0;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: allowMultiple ? 10 : 1,
    multiple: allowMultiple,
    disabled: !canUpload || isUploading
  });

  // Get translated display name and description
  const displayName = config?.nameKey 
    ? t(config.nameKey, { defaultValue: fileKey.replace(/_/g, ' ') })
    : fileKey.replace(/_/g, ' ');
  const description = config?.descriptionKey 
    ? t(config.descriptionKey, { defaultValue: '' })
    : '';
  const multipleHint = config?.multipleHint
    ? t(config.multipleHint, { defaultValue: '' })
    : '';

  return (
    <Card className={hasUploaded ? 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {displayName}
            </CardTitle>
            {description && (
              <CardDescription className="mt-1">
                {description}
              </CardDescription>
            )}
          </div>
          {hasUploaded ? (
            <Badge variant="default" className="bg-green-600 hover:bg-green-600">
              <Check className="h-3 w-3 mr-1" />
              {uploadedCount > 1 ? `${uploadedCount} filer` : 'Uppladdad'}
            </Badge>
          ) : (
            <Badge variant="secondary">Saknas</Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Expected fields info */}
        {config?.expectedFields && config.expectedFields.length > 0 && (
          <div className="mb-3 text-xs text-muted-foreground">
            <span className="font-medium">Förväntade fält:</span>{' '}
            {config.expectedFields.join(', ')}
          </div>
        )}

        {/* Example data */}
        {config?.exampleData && (
          <div className="mb-3 text-xs">
            <span className="text-muted-foreground">Dataexempel:</span>{' '}
            <code className="bg-muted px-1.5 py-0.5 rounded text-[10px]">
              {config.exampleData}
            </code>
          </div>
        )}

        {/* Multiple files hint */}
        {allowMultiple && multipleHint && (
          <div className="mb-3 text-xs text-blue-600 dark:text-blue-400">
            <span className="font-medium">Tips:</span> {multipleHint}
          </div>
        )}

        {/* Uploaded files list */}
        {uploadedFiles.length > 0 && (
          <div className="mb-4 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">Uppladdade filer:</p>
            {uploadedFiles.map((file) => (
              <div 
                key={file.id} 
                className="flex items-center gap-2 text-xs bg-muted/50 rounded px-2.5 py-1.5"
              >
                <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                <span className="truncate flex-1 font-medium">{file.fileName}</span>
                <span className="text-muted-foreground flex-shrink-0">
                  {formatFileSize(file.sizeBytes)}
                </span>
                {file.isFromThisLink && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                    Just nu
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Active uploads */}
        {activeUploads.length > 0 && (
          <div className="mb-4 space-y-1.5">
            {activeUploads.map((upload, i) => (
              <div 
                key={i} 
                className="flex items-center gap-2 text-xs bg-primary/5 rounded px-2.5 py-1.5"
              >
                <Loader2 className="h-3 w-3 animate-spin text-primary flex-shrink-0" />
                <span className="truncate flex-1">{upload.fileName || 'Laddar upp...'}</span>
              </div>
            ))}
          </div>
        )}

        {/* Upload area */}
        {canUpload && (
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
              transition-colors duration-200
              ${isDragActive 
                ? 'border-primary bg-primary/5' 
                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
              }
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            <div className="space-y-2">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm">
                {isDragActive 
                  ? (allowMultiple ? 'Släpp filerna här...' : 'Släpp filen här...')
                  : hasUploaded && allowMultiple
                    ? 'Ladda upp ytterligare filer'
                    : allowMultiple
                      ? 'Dra och släpp filer här, eller klicka för att välja'
                      : 'Dra och släpp fil här, eller klicka för att välja'
                }
              </p>
              {config?.formats && (
                <p className="text-xs text-muted-foreground">
                  Godkända format: {config.formats.join(', ')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Show uploaded count for single-file types */}
        {!canUpload && hasUploaded && !allowMultiple && uploadedFiles.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            ✓ Fil uppladdad
          </p>
        )}
      </CardContent>
    </Card>
  );
}
